import { createSelector } from "@reduxjs/toolkit";

/**
 * Memoized selectors for OverSight Redux store.
 *
 * All selectors that derive data from the systemId-keyed store live here.
 * Components should use the custom hooks in ../hooks/ which wrap these selectors.
 */

// ─── Base Selectors ──────────────────────────────────────────────────────────

// ─── Config Tier ─────────────────────────────────────────────────────────────

/** Select system config */
export const selectSystemConfig = (state, systemId) => state.config[systemId]?.system?.config;

/** Select network connection status */
export const selectIsNetworkConnected = (state, systemId) => state.config[systemId]?.system?.isNetworkConnected;

/** Select latest toast alert */
export const selectLatestAlert = (state, systemId) => state.config[systemId]?.system?.latestAlert;

/** Select toast alerts array */
export const selectToastAlerts = (state, systemId) => state.config[systemId]?.system?.toastAlerts;

/** Select device configs (formatted: hwId → valueName → config) */
export const selectDevicesConfigs = (state, systemId) => state.config[systemId]?.devicesConfigs?.devicesConfigs;

/** Select raw device config entries */
export const selectDeviceConfigEntries = (state, systemId) => state.config[systemId]?.devicesConfigs?.entries;

/** Select settings config (which settings views are available) */
export const selectSettingsConfig = (state, systemId) => state.config[systemId]?.devicesConfigs?.settingsConfig;

/** Select reports config */
export const selectReportsConfig = (state, systemId) => state.config[systemId]?.devicesConfigs?.reportsConfig;

/** Select graphics configs */
export const selectGraphicsConfigs = (state, systemId) => state.config[systemId]?.devicesConfigs?.graphicsConfigs;

/** Select samples configs */
export const selectSamplesConfigs = (state, systemId) => state.config[systemId]?.devicesConfigs?.samplesConfigs;

/** Select list view configs */
export const selectListViewConfigs = (state, systemId) => state.config[systemId]?.devicesConfigs?.listViewConfigs;

/** Select device parameters */
export const selectDevicesParameters = (state, systemId) => state.config[systemId]?.devicesParameters;

/** Select selected devices parameters */
export const selectSelectedDevicesParameters = (state, systemId) =>
  state.config[systemId]?.devicesParameters?.selectedDevicesParameters;

/** Select solution type (cloud only) */
export const selectSolutionType = (state, systemId) => state.config[systemId]?.solutionType;

/** Select settings data */
export const selectSettings = (state, systemId) => state.config[systemId]?.settings;

/** Select GeneralSettings formatted json data */
export const selectGeneralSettingsJson = (state, systemId) =>
  state.config[systemId]?.settings?.GeneralSettings?.formattedEntries?.jsonData;

/** Select sample entries configs */
export const selectSampleEntries = (state, systemId) => state.config[systemId]?.devicesConfigs?.sampleEntries;

// ─── Realtime Tier ───────────────────────────────────────────────────────────

/** Select raw device data */
export const selectDevices = (state, systemId) => state.realtime[systemId]?.devices;

/** Select formatted device entries (hwId → valueName → value) */
export const selectFormattedEntries = (state, systemId) => state.realtime[systemId]?.devices?.formattedEntries;

/** Select extra diagnostics (values without config) */
export const selectExtraDiagnostics = (state, systemId) => state.realtime[systemId]?.devices?.extraDiagnostics;

/** Select raw sensor data (LoRa cloud) */
export const selectRawSensorData = (state, systemId) => state.realtime[systemId]?.devices?.rawSensorData;

/** Select device data timestamp */
export const selectDataTimeStamp = (state, systemId) => state.realtime[systemId]?.devices?.dataTimeStamp;

/** Select last raw socket data batch (for live trend subscription) */
export const selectLastSocketBatch = (state, systemId) => state.realtime[systemId]?.devices?.lastSocketBatch;

/** Select alarm entries (grouped by hwId) */
export const selectAlarmEntries = (state, systemId) => state.realtime[systemId]?.alarms?.entries;

/** Select formatted alarm entries */
export const selectAlarmFormattedEntries = (state, systemId) => state.realtime[systemId]?.alarms?.formattedEntries;

/** Select socket connection status */
export const selectSocketConnected = (state, systemId) => state.realtime[systemId]?.socket?.isConnected;

/** Select samples */
export const selectSamples = (state, systemId) => state.realtime[systemId]?.samples;

/** Select formatted sample entries (hwId → valueName → value) */
export const selectSampleFormattedEntries = (state, systemId) => state.realtime[systemId]?.samples?.formattedEntries;

/** Select sample extra diagnostics */
export const selectSampleExtraDiagnostics = (state, systemId) => state.realtime[systemId]?.samples?.extraDiagnostics;

// ─── Historical Tier ─────────────────────────────────────────────────────────

/** Select trends data */
export const selectTrends = (state, systemId) => state.historical[systemId]?.trends;

/** Select chart data list (all 4 charts) */
export const selectChartDataList = (state, systemId) => state.historical[systemId]?.trends?.chartDataList;

/** Select chart data for a specific chart */
export const selectChartData = (state, systemId, chart) => state.historical[systemId]?.trends?.chartDataList?.[chart];

/** Select trend loading status */
export const selectTrendLoading = (state, systemId) => state.historical[systemId]?.trends?.isTrendLoading;

/** Select InfluxDB data loading status */
export const selectDataLoadingStatus = (state, systemId) => state.historical[systemId]?.trends?.dataLoadingStatus;

/** Select selected datetime filter (Live/Hour/Day/Week/Month/Year/Custom) */
export const selectSelectedDateTimeFilter = (state, systemId) => state.historical[systemId]?.trends?.selectedDateTimeFilter;

/** Select time filter from offset */
export const selectTimeFilterFrom = (state, systemId) => state.historical[systemId]?.trends?.timeFilterFrom;

/** Select time filter to offset */
export const selectTimeFilterTo = (state, systemId) => state.historical[systemId]?.trends?.timeFilterTo;

/** Select direct timestamp from (unix) */
export const selectDirectTimestampFrom = (state, systemId) => state.historical[systemId]?.trends?.directTimestampFrom;

/** Select direct timestamp to (unix) */
export const selectDirectTimestampTo = (state, systemId) => state.historical[systemId]?.trends?.directTimestampTo;

/** Select devices filter list (chart1-4 → hwId → valueName → config) */
export const selectDevicesFilterList = (state, systemId) => state.historical[systemId]?.trends?.devicesFilterList;

/** Select devices live list */
export const selectDevicesLiveList = (state, systemId) => state.historical[systemId]?.trends?.devicesLiveList;

/** Select alarms history */
export const selectAlarmsHistory = (state, systemId) => state.historical[systemId]?.alarmsHistory;

/** Select alarm limits */
export const selectAlarmsLimits = (state, systemId) => state.historical[systemId]?.alarmsLimits;

/** Select reports */
export const selectReports = (state, systemId) => state.historical[systemId]?.reports;

// ─── UI Tier ─────────────────────────────────────────────────────────────────

/** Select UI state */
export const selectUI = (state, systemId) => state.ui[systemId]?.ui;

/** Select just the diagnostics picker list (narrow slice of UI state).
 *  Used as a memoized-selector input instead of the whole `selectUI` object so that
 *  unrelated ui-slice updates (e.g. commandsStatusList, lastUserCommandFromOverview,
 *  serviceMode) don't invalidate the devicesList/samplesList memoization. Using the whole
 *  `ui` object previously caused an infinite dispatch loop: opening the commands list for a
 *  DIGI_SCU/SO2_SCRUBBER device triggers a commandsStatusList update, which (via the broad
 *  selectUI input) produced a brand new devicesList/diagnostics reference every time, which
 *  re-triggered the same commandsStatusList update effect, forever. */
export const selectDiagnosticsPickerList = (state, systemId) => state.ui[systemId]?.ui?.overview?.diagnosticsPickerList;

/** Select language */
export const selectLanguage = (state, systemId) => state.ui[systemId]?.ui?.settings?.language;

/** Select service mode state */
export const selectServiceMode = (state, systemId) => state.ui[systemId]?.ui?.serviceMode;

/** Select overview UI state */
export const selectOverviewUI = (state, systemId) => state.ui[systemId]?.ui?.overview;

/** Select edge unit status info */
export const selectEdgeUnitStatusInfo = (state, systemId) => state.ui[systemId]?.ui?.edgeUnitStatusInfo;

/** Select settings UI toggles */
export const selectSettingsUI = (state, systemId) => state.ui[systemId]?.settingsUI;

/** Select graphics state */
export const selectGraphics = (state, systemId) => state.ui[systemId]?.graphics;

// selectSamplesList removed — use makeSelectSamplesListForUI() memoized selector instead

// ─── Cloud Selectors ─────────────────────────────────────────────────────────

/** Select customers list (cloud only) */
export const selectCustomers = (state) => state.customers;

/** Select customers list */
export const selectCustomersList = (state) => state.customers?.list;

/** Select selected systems in list view (cloud only) */
export const selectSelectedSystems = (state) => state.customers?.selectedSystems;

/** Select selected map systems (cloud only) */
export const selectSelectedMapSystems = (state) => state.customers?.selectedMapSystems;

/** Select selected sensor site (cloud only) */
export const selectSelectedSensorSite = (state) => state.customers?.selectedSensorSite;

/** Select edit view state (cloud only) */
export const selectEditView = (state) => state.customers?.editView;

/** Select edit view customerId (cloud only) */
export const selectEditViewCustomerId = (state) => state.customers?.editView?.customerId;
export const selectEditViewSiteName = (state) => state.customers?.editView?.selectedSiteName;
export const selectEditViewSelectedDeviceId = (state) => state.customers?.editView?.selectedDeviceId;

/** Select edit view enabled flag (cloud only) */
export const selectEditViewEnabled = (state) => state.customers?.editView?.editViewEnabled;

/** Select create customer view flag (cloud only) */
export const selectCreateCustomerView = (state) => state.customers?.createCustomerView;

/** Select create device view flag (cloud only) */
export const selectCreateDeviceView = (state) => state.customers?.createDeviceView;

// ─── Derived / Memoized Selectors ────────────────────────────────────────────

/** Base selectors for alarm entity adapter state */
const selectAlarmIds = (state, systemId) => state.realtime[systemId]?.alarms?.ids;
const selectAlarmEntities = (state, systemId) => state.realtime[systemId]?.alarms?.entities;

/** Select all alarms flat array (from entity adapter: ids + entities → array) — memoized */
export const selectAllAlarms = createSelector(
  [selectAlarmIds, selectAlarmEntities],
  (ids, entities) => {
    if (!ids || ids.length === 0) return [];
    return ids.map((id) => entities[id]).filter(Boolean);
  }
);

/** Select alarms grouped by hwId (legacy alias of entries) */
export const selectAlarmsByHwId = (state, systemId) => state.realtime[systemId]?.alarms?.entries;

/** Select active alarms only — memoized */
export const makeSelectActiveAlarms = () =>
  createSelector(
    [selectAllAlarms],
    (allAlarms) => {
      if (!allAlarms || allAlarms.length === 0) return [];
      return allAlarms.filter((a) => a.isActive);
    }
  );

/** Select active device alarms — memoized */
export const makeSelectDeviceAlarms = () =>
  createSelector(
    [selectAllAlarms],
    (allAlarms) => {
      if (!allAlarms) return [];
      return allAlarms.filter((a) => a.isActive && a.category === "Device");
    }
  );

/** Select active sample alarms — memoized */
export const makeSelectSampleAlarms = () =>
  createSelector(
    [selectAllAlarms],
    (allAlarms) => {
      if (!allAlarms) return [];
      return allAlarms.filter((a) => a.isActive && a.category === "SampleCondition");
    }
  );

/** Select active warning alarms — memoized */
export const makeSelectWarningAlarms = () =>
  createSelector(
    [selectAllAlarms],
    (allAlarms) => {
      if (!allAlarms) return [];
      return allAlarms.filter((a) => a.isActive && a.category === "Warning");
    }
  );

/** Select active alarm count by category — memoized */
export const makeSelectAlarmCounts = () =>
  createSelector(
    [selectAllAlarms],
    (allAlarms) => {
      if (!allAlarms || allAlarms.length === 0) {
        return { devices: 0, samples: 0, warnings: 0, total: 0 };
      }
      let devices = 0;
      let samples = 0;
      let warnings = 0;
      allAlarms.forEach((alarm) => {
        if (alarm.isActive) {
          if (alarm.category === "Device") devices++;
          else if (alarm.category === "SampleCondition") samples++;
          else if (alarm.category === "Warning") warnings++;
        }
      });
      return { devices, samples, warnings, total: devices + samples + warnings };
    }
  );

/** Select alarms for a specific category — memoized */
export const makeSelectAlarmsByCategory = () =>
  createSelector(
    [selectAllAlarms, (_state, _systemId, category) => category],
    (allAlarms, category) => {
      if (!allAlarms || !category) return [];
      // "alarms" = no chip selected → show all ACTIVE alarms (matches the other categories,
      // which are also active-only). Inactive alarms are never rendered here.
      if (category === "alarms") return allAlarms.filter((a) => a.isActive);
      const categoryMap = {
        devices: "Device",
        samples: "SampleCondition",
        warnings: "Warning",
      };
      const mapped = categoryMap[category];
      if (!mapped) return allAlarms.filter((a) => a.isActive);
      return allAlarms.filter((a) => a.isActive && a.category === mapped);
    }
  );

/** Derive devicesList from formattedEntries + ui + alarms + extraDiagnostics — memoized.
 *  Replaces the old pattern of storing devicesList in Redux. */
export const makeSelectDevicesListForUI = () => {
  // Lazy-import to avoid circular dependency (selectors ↔ utils)
  let _generateDeviceListForUI = null;
  const getGenerator = () => {
    if (!_generateDeviceListForUI) {
      _generateDeviceListForUI = require("../helpers/utils").generateDeviceListForUI;
    }
    return _generateDeviceListForUI;
  };

  return createSelector(
    [selectFormattedEntries, selectDiagnosticsPickerList, selectAlarmEntries, selectExtraDiagnostics],
    (formattedEntries, diagnosticsPickerList, alarmsEntries, extraDiagnostics) => {
      if (!formattedEntries || Object.keys(formattedEntries).length === 0) return [];
      return getGenerator()(formattedEntries, false, { overview: { diagnosticsPickerList: diagnosticsPickerList || [] } }, alarmsEntries, extraDiagnostics);
    }
  );
};

/** Derive samplesList from sample formattedEntries + ui + alarms + extraDiagnostics — memoized.
 *  Replaces the old pattern of storing samplesList in Redux. */
export const makeSelectSamplesListForUI = () => {
  let _generateSampleListForUI = null;
  const getGenerator = () => {
    if (!_generateSampleListForUI) {
      _generateSampleListForUI = require("../helpers/utils").generateSampleListForUI;
    }
    return _generateSampleListForUI;
  };

  return createSelector(
    [selectSampleFormattedEntries, selectDiagnosticsPickerList, selectAlarmEntries, selectSampleExtraDiagnostics],
    (formattedEntries, diagnosticsPickerList, alarmsEntries, extraDiagnostics) => {
      if (!formattedEntries || Object.keys(formattedEntries).length === 0) return [];
      return getGenerator()(formattedEntries, false, { overview: { diagnosticsPickerList: diagnosticsPickerList || [] } }, alarmsEntries, extraDiagnostics);
    }
  );
};
